import { Component } from "react";
import { connect, ConnectedComponent } from "react-redux";
import * as StoreTypes from "../../../DataTypes/storeTypes";
import * as ClientTypes from "../../../DataTypes/clientTypes";
import * as OrganisationInfo from "../../../../Common/organisationInfo";
import * as ConfirmationActions from "../../../Actions/confirmationActions";
import * as ServiceActions from "../../../Actions/serviceActions";
import * as LocaleHelper from "../../../../Common/localeHelper";
import * as DiscountHelper from "../../../Helpers/discountHelper";
import { CreditCardFormConnected } from "./CreditCardForm";
import { selectIsStripeConnected, selectPortalPaymentMode } from "../../../Helpers/selectors";
import { Button } from "@powerdiary/components-library/lib";

interface IProps {
    servicePrice: number;
    amount: number;
    totalAmount: number;
    currencySymbol: string;
    stripeMode: OrganisationInfo.ClientPortalPaymentModes;
    showStripeFields: boolean;
    termsAndConditions: string;
    discount: number;
    discountType: ClientTypes.DiscountType;
    isPortalDeposit: boolean;
    isPaymentEnabled: boolean;
    onUseTotalPayment();
    showStripe();
}

class Form extends Component<IProps> {
    constructor(props) {
        super(props);
    }

    render() {
        const payModes = OrganisationInfo.ClientPortalPaymentModes;
        let { servicePrice, amount, isPortalDeposit, totalAmount, isPaymentEnabled } = this.props;
        const formattedFull = LocaleHelper.amountToCurrency(totalAmount, this.props.currencySymbol);
        const payNowText = isPortalDeposit ? `Pay full ${formattedFull}` : "Pay now";
        const shouldDisplayPaymentButton =
            isPaymentEnabled &&
            this.props.stripeMode === payModes.Optional &&
            !this.props.showStripeFields &&
            amount > 0;
        const shouldDisplayPaymentForm =
            isPaymentEnabled &&
            (this.props.stripeMode === payModes.Required || isPortalDeposit || this.props.showStripeFields) &&
            amount > 0;

        const getAmount = () => {
            if (shouldDisplayPaymentForm) {
                return isPortalDeposit && !isPaymentEnabled ? totalAmount : amount;
            } else {
                return servicePrice;
            }
        };

        return (
            <>
                <div className="row h-100">
                    <div className="col-12 h-100">
                        <div className="payment-details-box h-100">
                            {/**
                             * NOTE: because of a matter of design, we needed to move the FORM tag
                             * to an ancestor to be able to achieve the nice looking buttons design
                             */}

                            {amount > 0 && (
                                <div className="amount-box">
                                    <label>
                                        {isPortalDeposit && isPaymentEnabled ? "Make a deposit of" : "Amount"}
                                    </label>

                                    <div className="amount">
                                        {LocaleHelper.amountToCurrency(getAmount(), this.props.currencySymbol)}
                                    </div>
                                    {this.props.discount > 0 ? (
                                        <div className="discount">
                                            discount:{" "}
                                            {DiscountHelper.discountText(
                                                this.props.discount,
                                                this.props.discountType,
                                                this.props.currencySymbol
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            )}
                            {shouldDisplayPaymentButton && (
                                <div className="form-group mt-3 text-center">
                                    {isPortalDeposit ? (
                                        <label className="text-uppercase d-block">Or you can pay full now</label>
                                    ) : null}
                                    <Button
                                        variant="contained"
                                        size="extraSmall"
                                        onClick={isPortalDeposit ? this.props.onUseTotalPayment : this.props.showStripe}
                                    >
                                        {payNowText}
                                    </Button>
                                </div>
                            )}
                            {shouldDisplayPaymentForm && <CreditCardFormConnected />}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: StoreTypes.IStore, ownProps: IProps): IProps => {
    const info = state.stepsInfo[state.common.currentStep] as StoreTypes.IConfirmationStepInfo;
    const organisationInfo = state.initialSettings.accountInitialSettings.organisationInfo;
    const currentClient = state.common.clients.find((x) => x.id === info.clientId);
    const serviceInfo = state.stepsInfo[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

    return {
        ...ownProps,
        stripeMode: selectPortalPaymentMode(state),
        servicePrice: serviceInfo.amount,
        amount: serviceInfo.displayAmount ? serviceInfo.displayAmount : 0,
        totalAmount: serviceInfo.displayTotalAmount ? serviceInfo.displayTotalAmount : 0,
        currencySymbol: organisationInfo.currencySymbol,
        showStripeFields: info.showStripe,
        termsAndConditions: organisationInfo.termsAndConditions,
        discount: currentClient ? currentClient.discount : 0,
        discountType: currentClient ? currentClient.discountType : ClientTypes.DiscountType.Percentage,
        isPortalDeposit: serviceInfo.isPortalDeposit,
        isPaymentEnabled: selectIsStripeConnected(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): IProps => {
    return {
        ...ownProps,
        onUseTotalPayment: () => {
            dispatch(ServiceActions.applyFullPayment());
            dispatch(ConfirmationActions.showStripe(true));
        },
        showStripe: () => {
            dispatch(ConfirmationActions.showStripe(true));
        },
    };
};

export const PaymentFormConnected: ConnectedComponent<typeof Form, any> = connect(
    mapStateToProps,
    mapDispatchToProps
)(Form);
